import React, {useCallback, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import Button from "../../base/button/button";
import Grid2 from "@mui/material/Unstable_Grid2";
import Form, {MuiRFF, Yup} from "../../form";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import {useTranslation} from "react-i18next";
import ExternalVendorAutocomplete from "../../text-fields/autocomplete/vendor-autocomplete/external-vendor";

/**
 * Imports a vendor from Spire by selecting an external vendor (code → CleanAF/Vendor/Import).
 */
const ImportExternalVendorDialog = ({open, setOpen, onSuccess}) => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.vendors-list.importDialog'});
    const [importing, setImporting] = useState(false);
    const isMounted = useIsMounted();

    const schema = useMemo(() => Yup.object().shape({
        externalVendor: Yup.mixed().nullable().required(ValidateMessages.required)
            .test('has-code', ValidateMessages.required, (value) => Boolean(value?.code)),
    }), []);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        externalVendor: null,
    }), []);

    /**
     * Imports the selected external vendor by code.
     */
    const submitForm = useCallback(async (values) => {
        const code = values.externalVendor?.code?.toString()?.trim();
        if (!code)
            return;
        setImporting(true);
        const response = await Api.importVendorFromExternalService({code});
        if (!isMounted())
            return;
        setImporting(false);
        if (response?.resultFlag) {
            onSuccess?.(response.data);
            setOpen(false);
        }
    }, [isMounted, onSuccess, setOpen]);

    return (
        <Dialog open={open} setOpen={setOpen} maxWidth={'sm'}>
            <DialogTitle>{t('title')}</DialogTitle>
            <DialogContent dividers sx={{paddingBlock: 4}}>
                <DialogContentText variant={'body2'} sx={{marginBlockEnd: 2}}>
                    {t('description')}
                </DialogContentText>
                <Form
                    onSubmit={submitForm}
                    validate={validate}
                    initialValues={initialValues}
                    formProps={{id: 'import-external-vendor-form'}}
                >
                    {() => (
                        <Grid2 container spacing={2}>
                            <Grid2 xs={12}>
                                <ExternalVendorAutocomplete
                                    form
                                    name={'externalVendor'}
                                    label={t('externalVendor')}
                                    required={required.externalVendor}
                                    disabled={importing}
                                />
                            </Grid2>
                        </Grid2>
                    )}
                </Form>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'}}>
                    <Button
                        disabled={importing}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        disabled={importing}
                        variant={'dialog'}
                        color={'primary'}
                        type={'submit'}
                        form={'import-external-vendor-form'}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {importing ? t('importing') : t('import')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default ImportExternalVendorDialog;
